import React, { useEffect, useState } from 'react'
import { IOrder } from '../../interfaces/order';
import { useParams } from 'react-router-dom';
import { callApi } from '../../api/callApi';
import { API_ENDPOINT, API_URL, API_URL_2 } from '../../api/constant';
import Layout from '../../components/Layout/Layout';
import StatusDropDown from '../../components/common/OrderStatusDropdown';
import { ISettings } from '../../interfaces/settings';
import { formatDateTime, getPaymentMethodMessage, roundCurrency, showToastNotification, toFixedThree } from '../../utility/helper';
import useOrderApi from '../../hooks/useDownloadInvoice';
import Modal from '../../components/UI/Modal/Modal';
import AssignDriverModal from './AssignDriverModal';
import moment from '../../utility/moment-timezone';
import useConfirmationModal from '../../hooks/useConfirmationModal';
import ConfirmationModal from '../../components/UI/Modal/ConfirmationModal';
import { IMeateryBox } from '../../interfaces/meateryBox';
import { IProduct } from '../../interfaces/product';
import RefundRequestCard from './RefundRequestCard';
import OrderItemUnitPrice from '../../components/common/OrderItemUnitPrice';
import {
    RefundQuote,
    buildLegacyRefundPayload,
    buildRefundPayload,
    getItemModifiers,
    getOrderItemDisplay,
    getOrderItemKind,
    resolveUnitPricing,
} from '../../utility/refund';

const OrderDetail: React.FC = () => {
    const { showModal, modalProps, confirmAction } = useConfirmationModal();
    const { id } = useParams()
    const [loading, setLoading] = useState<boolean>(false);
    const [orderDetail, setOrderDetail] = useState<IOrder>();
    const [settings, setSettings] = useState<ISettings | null>(null);
    const [driverModal, setDriverModal] = useState(false)
    const { downloadInvoice } = useOrderApi();
    const [rejectOrder, setRejectOrder] = useState({
        show: false,
        rejectReason: '',
        error: ''
    });

    const [delayReasonModal, setDelayReasonModal] = useState({
        show: false,
        reason: '',
        error: ''
    });

    useEffect(() => {
        getOrderDetails();
        fetchSettings()
    }, [id])

    const getOrderDetails = async () => {
        try {
            setLoading(true);
            const response = await callApi(API_URL_2 + API_ENDPOINT.order + `orders/${id}`, { method: 'GET' });
            setOrderDetail(response);
        } catch (error: any) {
            console.error('Failed to fetch products:', error.message);
        } finally {
            setLoading(false);
        }
    }

    const fetchSettings = async () => {
        try {
            const response = await callApi(API_URL + API_ENDPOINT.auth + 'settings', { method: 'GET' });
            if (response && response.length > 0) {
                let setting = response[0]
                setSettings(setting)
            }

        } catch (error: any) {
            console.error('Failed to fetch settings:', error.message);
        }
    };

    /**
     * Accept a refund. The amount is priced by the shared refund engine, which
     * handles every product type (simple / modifiers / combo / Meatery Box),
     * partial quantities, discount + VAT allocation and the refundable balance.
     */
    const onAcceptRefund = (quote: RefundQuote) => {
        confirmAction({
            title: 'Confirm Refund',
            message: `Refund BHD ${toFixedThree(quote.total)} for ${quote.itemCount} item(s) to the customer’s Meatery wallet?`,
            onConfirm: () => updateRefundRequest('REFUNDED', buildRefundPayload(quote), buildLegacyRefundPayload(quote)),
        });
    }

    const onRejectRefund = () => {
        updateRefundRequest('REFUNDED REJECTED', { rejectReason: rejectOrder.rejectReason })
    }

    /**
     * @param postData fields sent alongside the status change.
     * @param legacyData minimal payload retried once if the API rejects the
     *        enriched one, so the refund itself can never fail on extra fields.
     */
    const updateRefundRequest = async (status: string, postData: any, legacyData?: any) => {
        try {
            await callApi(API_URL_2 + API_ENDPOINT.order + `orders/update-status`, {
                method: 'PUT',
                data: { id: id, status, ...postData }
            });
            getOrderDetails()
            showToastNotification('success', 'Order refund request successfully updated');
            setRejectOrder({ ...rejectOrder, show: false, rejectReason: '', error: '' })

        } catch (error: any) {
            if (legacyData) {
                console.warn('Refund detail payload rejected, retrying with the legacy payload:', error?.message);
                return updateRefundRequest(status, legacyData);
            }
            showToastNotification('danger', error?.message ?? 'Failed to update order refund request');
            console.error('Failed to update status:' + error?.message);
        }
    }

    const handleDelayReasonSubmit = async (reason: string) => {
        try {
            const response = await callApi(API_URL_2 + API_ENDPOINT.order + `orders/delay-reason/${id}`, {
                method: 'PUT',
                data: { reason: reason }
            });
            getOrderDetails()
            console.log("Response", response);
            // setOrderDetail(response)
            showToastNotification('success', 'Order refund request successfully updated');
            setDelayReasonModal({ ...rejectOrder, show: false, reason: '', error: '' })

        } catch (error: any) {
            showToastNotification('danger', error?.message ?? 'Failed to update order refund request');
            console.error('Failed to update status:' + error?.message);
        }
    };

    const revertCancellation = async () => {
        confirmAction({
            title: 'Confirm Revert',
            message: 'Are you sure you want to revert this order’s cancellation and restore it to active status?',
            onConfirm: () => handleRevertCancellation(),
        });
    }
    const handleRevertCancellation = async () => {
        try {
            await callApi(API_URL_2 + API_ENDPOINT.order + `orders/${id}/revert-status`, {
                method: 'PATCH'
            })
            getOrderDetails();
            showToastNotification('success', 'Order cancellation reverted successfully');
        } catch (error: any) {
            showToastNotification('danger', error?.message ?? 'Failed to revert order cancellation');
            console.error('Failed to revert order cancellation:' + error?.message);
        }
    }


    if (loading) return <Layout>Loading...</Layout>;

    if (!orderDetail) return <Layout>No Data Found</Layout>;
    const {
        personalInfo,
        // driverDetail,
        refundInfo,
        address,
        paymentMethod,
        orderItems,
        deliveryStatus,
        status,
        orderId,
        gift,
        total,
        // delayReason,
        grandTotal,
        // taxRate,
        // withTaxAmount,
        paidByWallet,
        deliveryFees,
        deliveryTime,
        previousStatus,
        createdAt,
        // driverRating,
        specialInstruction,
        discountedValue
    } = orderDetail;

    return (
        <Layout>
            <div className="mt-2">
                <div className='d-flex justify-content-between align-items-center gap-3 flex-wrap mb-3'>
                    <h1 className="mb-0 app-page-title">Order Details</h1>
                    <div className='d-flex gap-2 gap-md-3 align-items-center flex-wrap'>
                        {/* {status === 'ACCEPTED' && !driverDetail && <button onClick={() => setDriverModal(true)} className='btn app-btn-primary d-flex align-items-center gap-1'><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M6 0C6.13261 0 6.25979 0.0526785 6.35355 0.146447C6.44732 0.240215 6.5 0.367392 6.5 0.5V5.5H11.5C11.6326 5.5 11.7598 5.55268 11.8536 5.64645C11.9473 5.74021 12 5.86739 12 6C12 6.13261 11.9473 6.25979 11.8536 6.35355C11.7598 6.44732 11.6326 6.5 11.5 6.5H6.5V11.5C6.5 11.6326 6.44732 11.7598 6.35355 11.8536C6.25979 11.9473 6.13261 12 6 12C5.86739 12 5.74021 11.9473 5.64645 11.8536C5.55268 11.7598 5.5 11.6326 5.5 11.5V6.5H0.5C0.367392 6.5 0.240215 6.44732 0.146447 6.35355C0.0526785 6.25979 0 6.13261 0 6C0 5.86739 0.0526785 5.74021 0.146447 5.64645C0.240215 5.55268 0.367392 5.5 0.5 5.5H5.5V0.5C5.5 0.367392 5.55268 0.240215 5.64645 0.146447C5.74021 0.0526785 5.86739 0 6 0Z" fill="white" />
                        </svg> Assign Driver</button>} */}
                        {status === 'CANCELLED' && <button className='btn app-btn-primary d-flex align-items-center gap-1' onClick={() => revertCancellation()} >
                            Revert Cancellation
                        </button>}
                        <div className="" style={{ cursor: 'pointer' }} onClick={() => downloadInvoice(orderDetail.id || '')} >
                            <span className="badge text-bg-warning d-flex align-items-center gap-2 py-2 px-3">
                                View Order Confirmation
                                <i className='bi bi-eye' />
                            </span>
                        </div>
                        <h6 className='mb-0 text-nowrap'>Update Status</h6>
                        <StatusDropDown
                            CurrentStatus={status}
                            orderId={orderDetail.id}
                            orderStatus={settings?.orderStatus || []}
                            previousStatus={orderDetail.previousStatus}
                            // driverDetail={orderDetail.driverDetail}
                            updateStatus={() => getOrderDetails()} />
                    </div>
                </div>
                <div className="row">
                    {/* Order Refund Request — item selection, pricing and history */}
                    {refundInfo &&
                        <div className="col-lg-12 col-12">
                            <RefundRequestCard
                                order={orderDetail}
                                loading={loading}
                                onAccept={onAcceptRefund}
                                onReject={() => setRejectOrder({ ...rejectOrder, show: true })}
                            />
                        </div>
                    }
                    <div className="col-lg-8 col-12">
                        <div className='app-card app-card-orders-table shadow-sm mb-4 p-3 p-md-4'>
                            <div className="app-card-body">
                                <div className="">
                                    <h5 className='fw-bold'>Order Information</h5>
                                </div>
                                <div>

                                    <div className='row mb-1'>
                                        <strong className='col-md-6 col-12'>Order ID:</strong>
                                        <p className='mb-0 col-md-6 col-12 text-break'>{orderId}</p>
                                    </div>
                                    <div className='row mb-1'>
                                        <strong className='col-md-6 col-12'>Status:</strong>
                                        <p className='mb-0 col-md-6 col-12 text-break'> {status}</p>
                                    </div>
                                    <div className='row mb-1'>
                                        <strong className='col-md-6 col-12'>Delivery Time:</strong>
                                        <p className='mb-0 col-md-6 col-12 text-break'> {formatDateTime(deliveryTime || '')}</p>
                                    </div>
                                    <div className='row mb-1'>
                                        <strong className='col-md-6 col-12'>Delivery Status:</strong>
                                        <p className='mb-0 col-md-6 col-12 text-break'> {deliveryStatus || '-'}</p>
                                    </div>
                                    <div className='row mb-1'>
                                        <strong className='col-md-6 col-12'>Total:</strong>
                                        <p className='mb-0 col-md-6 col-12 text-break'> BHD {toFixedThree(total)}</p>
                                    </div>
                                    <div className='row mb-1'>
                                        <strong className='col-md-6 col-12'>Discount:</strong>
                                        <p className='mb-0 col-md-6 col-12 text-success'> BHD {toFixedThree(discountedValue)}</p>
                                    </div>

                                    <div className='row mb-1'>
                                        <strong className='col-md-6 col-12'>Delivery Fees:</strong>
                                        <p className='mb-0 col-md-6 col-12 text-break'> BHD {toFixedThree(deliveryFees)}</p>
                                    </div>
                                    <div className='row mb-1'>
                                        <strong className='col-md-6 col-12'>Pay By Wallet:</strong>
                                        <p className='mb-0 col-md-6 col-12 text-success'> BHD {toFixedThree(paidByWallet ?? 0)}</p>
                                    </div>
                                    {/* <div className='row mb-1'>
                                        <strong className='col-md-6 col-12'>VAT ({taxRate}%):</strong>
                                        <p className='mb-0 col-md-6 col-12 text-break'> BHD {toFixedThree(withTaxAmount - total)}</p>
                                    </div> */}
                                    <div className='row mb-1'>
                                        <strong className='col-md-6 col-12'>Grand Total:</strong>
                                        <p className='mb-0 col-md-6 col-12 text-break'> BHD {toFixedThree(grandTotal)}</p>
                                    </div>
                                    <div className='row mb-1'>
                                        <strong className='col-md-6 col-12'>Order Time:</strong>
                                        <p className='mb-0 col-md-6 col-12 text-break'> {moment(createdAt).format("DD MMM,YYYY hh:mm a")}</p>
                                    </div>
                                    <div className='row mb-1'>
                                        <strong className='col-md-6 col-12'>Payment Type:</strong>
                                        <p className='mb-0 col-md-6 col-12 text-break'>{getPaymentMethodMessage(paymentMethod.type)} </p>
                                    </div>
                                    <div className='row mb-1'>
                                        <strong className='col-md-6 col-12'>Transection ID:</strong>
                                        <p className='mb-0 col-md-6 col-12 text-break'>{orderDetail.chargeId} </p>
                                    </div>
                                    {previousStatus.find(s => s.status === 'DELIVERED') && <div className='mb-1 row'>
                                        <strong className='col-md-6 col-12'>Order Delivery Time:</strong>
                                        <p className='mb-0 col-md-6 col-12 text-break'> {moment(previousStatus.find(s => s.status === 'DELIVERED')?.dateTime).format("DD MMM,YYYY hh:mm a")}</p>
                                    </div>}
                                    {specialInstruction && <div className='row mb-1'>
                                        <strong className='col-md-6 col-12'>Special Instructions:</strong>
                                        <p className='mb-0 col-md-6 col-12 text-break'>{specialInstruction} </p>
                                    </div>}
                                    {/* {deliveryStatus === 'DELAYED' && !delayReason && (
                                        <div className="row mt-3">
                                            <div className="col-12 text-center">
                                                <button
                                                    className="btn btn-warning"
                                                    onClick={() => setDelayReasonModal(prev => ({ ...prev, show: true }))}
                                                >
                                                    Add Delivery Delay Reason
                                                </button>
                                            </div>
                                        </div>
                                    )} */}
                                </div>
                            </div>
                        </div>
                        <div className='app-card app-card-orders-table shadow-sm mb-4 p-3 p-md-4'>
                            <div className='app-card-body'>
                                <div className="mb-4">
                                    <h5 className="fw-bold">Delivery Address</h5>
                                    <div>
                                        {/* Address Type - Always show */}
                                        <div className='mb-1 row'>
                                            <strong className='col-md-6 col-12'>Address Type:</strong>
                                            <p className='mb-0 col-md-6 col-12 text-break'>
                                                {address.addressType}
                                            </p>
                                        </div>

                                        {/* Building Name - Show for all types if exists */}


                                        <>
                                            {address.aptNumber && (
                                                <div className='mb-1 row'>
                                                    <strong className='col-md-6 col-12'>House / Villa / Building No.:</strong>
                                                    <p className='mb-0 col-md-6 col-12 text-break'>
                                                        {address.aptNumber}
                                                    </p>
                                                </div>
                                            )}
                                            {address.buildingName && (
                                                <div className='mb-1 row'>
                                                    <strong className='col-md-6 col-12'>Apartment / Building Name:</strong>
                                                    <p className='mb-0 col-md-6 col-12 text-break'>
                                                        {address.buildingName}
                                                    </p>
                                                </div>
                                            )}
                                            {/* {address.floor && (
                                                <div className='mb-1 row'>
                                                    <strong className='col-md-6 col-12'>Floor:</strong>
                                                    <p className='mb-0 col-md-6 col-12 text-break'>
                                                        {address.floor}
                                                    </p>
                                                </div>
                                            )} */}
                                        </>


                                        {/* Common address fields for all types */}
                                        {address.street && (
                                            <div className='mb-1 row'>
                                                <strong className='col-md-6 col-12'>Road No.:</strong>
                                                <p className='mb-0 col-md-6 col-12 text-break'>
                                                    {address.street}
                                                </p>
                                            </div>
                                        )}

                                        {address.block && (
                                            <div className='mb-1 row'>
                                                <strong className='col-md-6 col-12'>Block:</strong>
                                                <p className='mb-0 col-md-6 col-12 text-break'>
                                                    {address.block}
                                                </p>
                                            </div>
                                        )}

                                        {address.area && (
                                            <div className='mb-1 row'>
                                                <strong className='col-md-6 col-12'>Area:</strong>
                                                <p className='mb-0 col-md-6 col-12 text-break'>
                                                    {address.area}
                                                </p>
                                            </div>
                                        )}

                                        {/* Company Name - Show only for Office if exists */}
                                        {address.addressType === 'Office' && address.companyName && (
                                            <div className='mb-1 row'>
                                                <strong className='col-md-6 col-12'>Company Name:</strong>
                                                <p className='mb-0 col-md-6 col-12 text-break'>
                                                    {address.companyName}
                                                </p>
                                            </div>
                                        )}

                                        {/* Additional Direction - Show for all types if exists */}
                                        {address.additionalDirection && (
                                            <div className='mb-1 row'>
                                                <strong className='col-md-6 col-12'>Additional Direction:</strong>
                                                <p className='mb-0 col-md-6 col-12 text-break'>
                                                    {address.additionalDirection}
                                                </p>
                                            </div>
                                        )}

                                        {/* Phone - Show for all types if exists */}
                                        {address.phone && (
                                            <div className='mb-1 row'>
                                                <strong className='col-md-6 col-12'>Phone:</strong>
                                                <p className='mb-0 col-md-6 col-12 text-break'>
                                                    {address.phone}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* <div className='app-card app-card-orders-table shadow-sm mb-4 p-3 p-md-4'>
                            <h5 className="fw-bold">Driver Information</h5>
                            <div className="">
                                <div className='mb-1 row'>
                                    <strong className='col-md-6 col-12'>Name:</strong>
                                    <p className='mb-0 col-md-6 col-12 text-break'> {driverDetail?.fullName || 'N/A'}</p>
                                </div>
                                <div className='mb-1 row'>
                                    <strong className='col-md-6 col-12'>Mobile:</strong>
                                    <p className='mb-0 col-md-6 col-12 text-break'>{driverDetail?.phone || 'N/A'}</p>
                                </div>
                                <div className='mb-1 row'>
                                    <strong className='col-md-6 col-12'>Email:</strong>
                                    <p className='mb-0 col-md-6 col-12 text-break'> {driverDetail?.email || 'N/A'}</p>
                                </div>
                                <div className='mb-1 row'>
                                    <strong className='col-md-6 col-12'>Order Delivery Time:</strong>
                                    <p className='mb-0 col-md-6 col-12 text-break'> {getTimeDifference("PICKED UP", "DELIVERED", previousStatus)}</p>
                                </div>

                                {deliveryStatus === 'DELAYED' && <div className='mb-1 row'>
                                    <strong className='col-md-6 col-12'>Order Delay Reason:</strong>
                                    <p className='mb-0 col-md-6 col-12 text-break'> {delayReason || 'N/A'}</p>
                                </div>}
                                {driverRating && <div className='mb-1 row'>
                                    <strong className='col-md-6 col-12'>Delivery Rating:</strong>
                                    <p className='mb-0 col-md-6 col-12 text-break'>
                                        <p
                                            className="star-rating"
                                            style={{ color: '#ffc107', fontSize: '1.2rem' }}
                                        >
                                            {'★'.repeat(Math.round(driverRating.rating)) +
                                                '☆'.repeat(5 - Math.round(driverRating.rating))}
                                        </p>
                                    </p>
                                </div>}
                                {driverRating && <div className='mb-1 row'>
                                    <strong className='col-md-6 col-12'>Delivery Review:</strong>
                                    <p className='mb-0 col-md-6 col-12 text-break'> {driverRating.review}</p>
                                </div>}
                            </div>
                        </div> */}
                    </div>
                    <div className="col-lg-4 col-12">
                        <div className='app-card app-card-orders-table shadow-sm mb-4 p-3 p-md-4'>
                            <h5 className="fw-bold">Customer Information</h5>
                            <div className="">
                                <div className='mb-2'>
                                    <strong>Name:</strong>
                                    <p className='mb-0 text-break'> {personalInfo?.firstName} {personalInfo?.lastName}</p>
                                </div>
                                <div className='mb-1'>
                                    <strong>Email:</strong>
                                    <p className='mb-0 text-break'> {personalInfo?.email}</p>
                                </div>

                            </div>
                        </div>
                        {gift && <div className='app-card app-card-orders-table shadow-sm mb-4 p-3 p-md-4'>
                            <h5 className="fw-bold">Gift Hamper Info</h5>
                            <div className="">
                                <div className='mb-2'>
                                    <p className='mb-0 text-break'> {gift.name}</p>
                                </div>
                                <div className='mb-1'>
                                    <img
                                        src={gift.image}
                                        alt={gift.image ?? ''}
                                        className="img-thumbnail w-100"
                                        style={{ maxHeight: '250px', objectFit: 'contain' }}
                                    />
                                </div>

                            </div>
                        </div>}

                    </div>
                    <div className='col-lg-12 col-12'>
                        <div className='app-card app-card-orders-table shadow-sm mb-4 p-3 p-md-4'>
                            <h5 className="fw-bold mb-3">Order Items ({orderItems.length})</h5>
                            <p className="small text-muted mb-3">
                                Modifier quantities and prices are per parent item. Their charges are included in the parent item’s net amount.
                            </p>

                            <div className="table-responsive">
                                <table className="table table-hover mb-0" style={{ minWidth: '900px' }}>
                                    <thead>
                                        <tr>
                                            <th scope="col" style={{ width: '50px' }}>#</th>
                                            <th scope="col">Product</th>
                                            <th scope="col">Details</th>
                                            <th scope="col" className="text-center text-nowrap" style={{ width: '100px' }}>Qty</th>
                                            <th scope="col" className="text-end text-nowrap" style={{ width: '230px' }}>Unit Price</th>
                                            <th scope="col" className="text-end text-nowrap" style={{ width: '140px' }}>Net Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {orderItems.map((item, index) => {
                                            const product: any = item.itemDetail?.product;
                                            // `itemDetail` can be empty — fall back to the line's own snapshot.
                                            const display = getOrderItemDisplay(item);
                                            const modifiers = getItemModifiers(item);
                                            const hasModifiers = modifiers.length > 0;
                                            // Product type resolution is shared with the refund engine.
                                            const kind = getOrderItemKind(item, modifiers);
                                            const isCombo = kind === 'COMBO';
                                            const isBox = kind === 'BOX';
                                            const isModifierProduct = (product?.modifierGroups?.length ?? 0) > 0 || hasModifiers;
                                            const modifiersTotal = roundCurrency(modifiers.reduce((s, m) => s + m.total, 0));
                                            const pricing = resolveUnitPricing(item, modifiersTotal, kind);

                                            return (
                                                <React.Fragment key={item.id}>
                                                    {/* Main Product Row */}
                                                    <tr className={isBox ? 'table-primary' : (isModifierProduct ? 'table-warning' : '')}>
                                                        <td className="align-middle">
                                                            <span className="">{index + 1}</span>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <div className="flex-shrink-0 me-3">
                                                                    <img
                                                                        src={display.image ?? undefined}
                                                                        alt={display.title}
                                                                        className="img-thumbnail"
                                                                        style={{ width: "60px", height: "60px", objectFit: "cover" }}
                                                                    />
                                                                </div>
                                                                <div className="flex-grow-1">
                                                                    <div className="fw-bold mb-1 text-break">{display.title}</div>
                                                                    <div className="d-flex flex-wrap gap-1">
                                                                        {
                                                                            item.addedFrom === 'checkout_recommendation' && (
                                                                                <span className="badge bg-success bg-opacity-10 text-success">
                                                                                    Checkout Suggested
                                                                                </span>
                                                                            )

                                                                        }
                                                                        {isBox && (
                                                                            <span className="badge bg-primary bg-opacity-10 text-primary">
                                                                                Meatery Box
                                                                            </span>
                                                                        )}
                                                                        {isCombo && (
                                                                            <span className="badge bg-warning bg-opacity-10 text-warning">
                                                                                Combo
                                                                            </span>
                                                                        )}
                                                                        {!isCombo && isModifierProduct && (
                                                                            <span className="badge bg-info bg-opacity-10 text-info">
                                                                                Customized
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            {isBox ? (
                                                                <div className="" style={{ fontSize: '0.9rem' }}>
                                                                    <div className="mb-1">
                                                                        Bundle containing {(product as IMeateryBox)?.items?.length || 0} items
                                                                    </div>
                                                                    {(product as IMeateryBox)?.additionalItems && (
                                                                        <div className="" style={{ fontSize: '0.85rem' }}>
                                                                            <strong>Additional Items:</strong> {(product as IMeateryBox)?.additionalItems}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <div className="product-details">
                                                                    <div className="d-flex flex-wrap gap-1 mb-1">
                                                                        {(product as IProduct)?.typeOfMeat?.map((meatType, idx) => (
                                                                            <span key={idx} className="badge bg-secondary bg-opacity-10 text-primary">
                                                                                {meatType}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                    <div className="" style={{ fontSize: '0.85rem' }}>
                                                                        {isCombo ? (
                                                                            <div className="mb-1">
                                                                                <strong>Type:</strong> Combo
                                                                            </div>
                                                                        ) : (
                                                                            <>
                                                                                <div className="mb-1">
                                                                                    <strong>Weight:</strong> {item.itemDetail?.pieceWeight || 'N/A'}
                                                                                </div>
                                                                                <div className="mb-1">
                                                                                    <strong>State:</strong> {(product as IProduct)?.state || 'N/A'}
                                                                                </div>
                                                                                <div className="mb-1">
                                                                                    <strong>Origin:</strong> {(product as IProduct)?.origin || 'N/A'}
                                                                                </div>
                                                                            </>
                                                                        )}
                                                                        {hasModifiers && (
                                                                            <div className="mb-1">
                                                                                <strong>Options:</strong> {modifiers.length} selected
                                                                                {modifiersTotal > 0 && <> · +BHD {toFixedThree(modifiersTotal)}</>}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="align-middle text-center">
                                                            <span className="fw-medium">{item.quantity}</span>
                                                        </td>
                                                        <td className="align-middle text-end text-nowrap">
                                                            <OrderItemUnitPrice
                                                                kind={kind}
                                                                unitBasePrice={pricing.unitBasePrice}
                                                                unitModifierTotal={modifiersTotal}
                                                                unitPrice={pricing.unitPrice}
                                                            />
                                                        </td>
                                                        <td className="align-middle text-end text-nowrap">
                                                            <div className="fw-bold">BHD {toFixedThree(roundCurrency(pricing.unitPrice * item.quantity))}</div>
                                                        </td>
                                                    </tr>

                                                    {/* Bundle Contents Rows (Meatery Box only) */}
                                                    {isBox && (product as IMeateryBox)?.items?.map((bundleItem) => (
                                                        <tr key={bundleItem.id} className="table-light">
                                                            <td></td>
                                                            <td>
                                                                <div className="d-flex align-items-center ms-4">
                                                                    <div className="flex-shrink-0 me-2">
                                                                        <img
                                                                            src={bundleItem.product.images?.[0]}
                                                                            alt={bundleItem.product.title}
                                                                            className="img-thumbnail"
                                                                            style={{ width: "40px", height: "40px", objectFit: "cover" }}
                                                                        />
                                                                    </div>
                                                                    <div className="flex-grow-1">
                                                                        <div className="fw-medium text-break" style={{ fontSize: '0.9rem' }}>
                                                                            {bundleItem.product.title}
                                                                        </div>
                                                                        <div className="" style={{ fontSize: '0.8rem' }}>
                                                                            Included in box
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="bundle-item-details" style={{ fontSize: '0.85rem' }}>
                                                                    <div className="">
                                                                        <strong>Qty in box:</strong> {bundleItem.quantity}
                                                                    </div>
                                                                    {bundleItem.product.productInfo?.[0]?.pieceWeight && (
                                                                        <div className="">
                                                                            <strong>Weight:</strong> {bundleItem.product.productInfo[0].pieceWeight}
                                                                        </div>
                                                                    )}
                                                                    {bundleItem.product.typeOfMeat?.length > 0 && (
                                                                        <div className="d-flex flex-wrap gap-1 mt-1">
                                                                            {bundleItem.product.typeOfMeat.map((meatType, idx) => (
                                                                                <span key={idx} className="badge bg-secondary bg-opacity-10 text-primary">
                                                                                    {meatType}
                                                                                </span>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="text-center">
                                                                <span className=" small">×{bundleItem.quantity}</span>
                                                            </td>
                                                            <td></td>
                                                            <td></td>
                                                        </tr>
                                                    ))}

                                                    {/* Selected Modifier Rows (products & combos) */}
                                                    {hasModifiers && modifiers.map((mod, mIdx) => (
                                                        <tr key={`${item.id}-mod-${mod.optionId || mIdx}`} className="table-light">
                                                            <td></td>
                                                            <td>
                                                                <div className="d-flex align-items-center ms-4">
                                                                    <div className="flex-shrink-0 me-2">
                                                                        {mod.image ? (
                                                                            <img
                                                                                src={mod.image}
                                                                                alt={mod.name}
                                                                                className="img-thumbnail"
                                                                                style={{ width: "40px", height: "40px", objectFit: "cover" }}
                                                                            />
                                                                        ) : (
                                                                            <div
                                                                                className="d-flex align-items-center justify-content-center bg-light border rounded"
                                                                                style={{ width: "40px", height: "40px" }}
                                                                            >
                                                                                <i className="bi bi-plus-circle text-muted" />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div className="flex-grow-1">
                                                                        <div className="fw-medium text-break" style={{ fontSize: '0.9rem' }}>
                                                                            {mod.name}
                                                                        </div>
                                                                        <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                                                                            {mod.groupName || 'Selected option'}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div style={{ fontSize: '0.85rem' }}>
                                                                    <div>
                                                                        <strong>Price:</strong>{' '}
                                                                        {mod.price === 0 ? (
                                                                            <span className="badge bg-success">Free</span>
                                                                        ) : (
                                                                            <>BHD {toFixedThree(mod.price)}</>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div style={{ fontSize: '0.85rem' }}>
                                                                    <div>
                                                                        <strong>Weight:</strong>{' '}
                                                                        {mod.weight || 'N/A'}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="text-center">
                                                                <span className=" small">×{mod.quantity}</span>
                                                            </td>
                                                            <td className="align-middle text-end text-nowrap">
                                                                {mod.total === 0 ? (
                                                                    <span className="text-success small">Free</span>
                                                                ) : (
                                                                    <span className="small">BHD {toFixedThree(mod.total)}</span>
                                                                )}
                                                            </td>
                                                            <td className="align-middle text-end text-nowrap">
                                                                <span className="text-muted small">Included above</span>
                                                            </td>
                                                        </tr>
                                                    ))}

                                                    {/* Separator Row */}
                                                    {index < orderItems.length - 1 && (
                                                        <tr>
                                                            <td colSpan={6} className="p-0">
                                                                <div className="border-bottom my-2"></div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
            <Modal
                show={rejectOrder.show}
                onClose={() => setRejectOrder({ show: false, rejectReason: '', error: '' })}
                title="Please Enter Reject Reason"
            >
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        if (!rejectOrder.rejectReason.trim()) {
                            setRejectOrder({ ...rejectOrder, error: 'Reject reason is required.' });
                            return;
                        }
                        onRejectRefund()
                    }}
                >
                    <div className="mb-3">
                        <label htmlFor="rejectReason" className="form-label">
                            Reject Reason
                        </label>
                        <textarea
                            id="rejectReason"
                            className="form-control"
                            rows={3}
                            placeholder="Enter your reason here"
                            value={rejectOrder.rejectReason}
                            onChange={(e) =>
                                setRejectOrder({ ...rejectOrder, rejectReason: e.target.value, error: '' })
                            }
                        />
                        {rejectOrder.error && (
                            <div className="text-danger mt-1">{rejectOrder.error}</div>
                        )}
                    </div>
                    <button type="submit" className="btn btn-primary">
                        Submit
                    </button>
                </form>
            </Modal>
            <Modal
                show={delayReasonModal.show}
                onClose={() => setDelayReasonModal({ show: false, reason: '', error: '' })}
                title="Please Enter Delay Reason"
            >
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        if (!delayReasonModal.reason.trim()) {
                            setDelayReasonModal(prev => ({ ...prev, error: 'Delay reason is required.' }));
                            return;
                        }
                        // Handle delay reason submission here
                        handleDelayReasonSubmit(delayReasonModal.reason);
                    }}
                >
                    <div className="mb-3">
                        <label htmlFor="delayReason" className="form-label">
                            Add Delivery Delay Details
                        </label>
                        <textarea
                            id="delayReason"
                            className="form-control"
                            rows={3}
                            placeholder="Enter the reason for the delay "
                            value={delayReasonModal.reason}
                            onChange={(e) =>
                                setDelayReasonModal(prev => ({
                                    ...prev,
                                    reason: e.target.value,
                                    error: ''
                                }))
                            }
                        />
                        {delayReasonModal.error && (
                            <div className="text-danger mt-1">{delayReasonModal.error}</div>
                        )}
                    </div>
                    <button type="submit" className="btn btn-primary">
                        Submit Reason
                    </button>
                </form>
            </Modal>
            <AssignDriverModal modal={driverModal} close={() => {
                setDriverModal(false);
                getOrderDetails()
            }} selectedOrders={[orderDetail.id]} />
            <ConfirmationModal {...modalProps} show={showModal} />
        </Layout>
    )
}

export default OrderDetail
